import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';
import Footer from '../../components/feature/Footer';
import ProductsFilterSidebar from './components/ProductsFilterSidebar';
import ProductCard from './components/ProductCard';
import EmptyState from './components/EmptyState';
import ErrorState from '../../components/base/ErrorState';

const supabaseUrl = import.meta.env.VITE_PUBLIC_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY;
const supabase = createClient(supabaseUrl, supabaseAnonKey);

interface Product {
  id: number;
  title: string;
  sku: string;
  brand_id: number | null;
  category_id: number | null;
  carton_size: number;
  carton_stock: number;
  main_images: string[];
  status: string;
  brand_name?: string;
  category_name?: string;
  min_price_pair?: number;
}

interface Brand {
  id: number;
  name: string;
}

interface Category {
  id: number;
  name: string;
}

type ViewMode = 'grid' | 'list';
type SortOption = 'default' | 'price_asc' | 'price_desc' | 'stock_desc';

const AllProductsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // State
  const [products, setProducts] = useState<Product[]>([]);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Filter & Sort params from URL
  const searchKeyword = searchParams.get('search') || '';
  const brandFilter = searchParams.get('brand') || '';
  const categoryFilter = searchParams.get('category') || '';
  const minStockFilter = searchParams.get('minStock') || '';
  const sortBy = (searchParams.get('sort') || 'default') as SortOption;
  const pageSize = parseInt(searchParams.get('pageSize') || '24');
  const currentPage = parseInt(searchParams.get('page') || '1');

  useEffect(() => {
    loadData();
  }, [searchKeyword, brandFilter, categoryFilter, minStockFilter, sortBy, pageSize, currentPage]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(false);

      // Load brands and categories for filters
      await Promise.all([
        loadBrands(),
        loadCategories(),
        loadProducts()
      ]);

      setLoading(false);
    } catch (error) {
      console.error('Error loading data:', error);
      setLoading(false);
      setError(true);
    }
  };

  const loadBrands = async () => {
    const { data, error } = await supabase
      .from('brands')
      .select('id, name')
      .order('name', { ascending: true });
    
    if (error) throw error;
    if (data) setBrands(data);
  };

  const loadCategories = async () => {
    const { data, error } = await supabase
      .from('categories')
      .select('id, name')
      .order('name', { ascending: true });
    
    if (error) throw error;
    if (data) setCategories(data);
  };

  const loadProducts = async () => {
    // Build query
    let query = supabase
      .from('products')
      .select('id, title, sku, brand_id, category_id, carton_size, carton_stock, main_images, status', { count: 'exact' })
      .eq('status', 'active');

    // Search filter (keyword)
    if (searchKeyword.trim()) {
      query = query.or(`title.ilike.%${searchKeyword}%,sku.ilike.%${searchKeyword}%`);
    }

    // Brand filter
    if (brandFilter) {
      query = query.eq('brand_id', parseInt(brandFilter));
    }

    // Category filter
    if (categoryFilter) {
      query = query.eq('category_id', parseInt(categoryFilter));
    }

    // Minimum stock filter
    if (minStockFilter) {
      const minStock = parseInt(minStockFilter);
      if (!isNaN(minStock) && minStock > 0) {
        query = query.gte('carton_stock', minStock);
      }
    }

    // Pagination
    const offset = (currentPage - 1) * pageSize;
    query = query.range(offset, offset + pageSize - 1);

    // Sorting
    if (sortBy === 'default') {
      query = query.order('id', { ascending: false });
    } else if (sortBy === 'stock_desc') {
      query = query.order('carton_stock', { ascending: false });
    }

    const { data: productsData, count, error } = await query;

    if (error) {
      console.error('Error loading products:', error);
      throw error;
    }

    if (!productsData) {
      setProducts([]);
      setTotalCount(0);
      return;
    }

    setTotalCount(count || 0);

    // Load brand names
    const brandIds = [...new Set(productsData.filter(p => p.brand_id).map(p => p.brand_id))];
    let brandsMap: Record<number, string> = {};
    if (brandIds.length > 0) {
      const { data: brandsData } = await supabase
        .from('brands')
        .select('id, name')
        .in('id', brandIds);
      
      if (brandsData) {
        brandsMap = brandsData.reduce((acc, b) => {
          acc[b.id] = b.name;
          return acc;
        }, {} as Record<number, string>);
      }
    }

    // Load category names
    const categoryIds = [...new Set(productsData.filter(p => p.category_id).map(p => p.category_id))];
    let categoriesMap: Record<number, string> = {};
    if (categoryIds.length > 0) {
      const { data: categoriesData } = await supabase
        .from('categories')
        .select('id, name')
        .in('id', categoryIds);
      
      if (categoriesData) {
        categoriesMap = categoriesData.reduce((acc, c) => {
          acc[c.id] = c.name;
          return acc;
        }, {} as Record<number, string>);
      }
    }

    // Load price tiers for all products
    const productIds = productsData.map(p => p.id);
    const { data: pricesData } = await supabase
      .from('price_tiers')
      .select('product_id, unit_price_pair')
      .in('product_id', productIds);

    // Calculate min price for each product
    const pricesMap: Record<number, number> = {};
    if (pricesData) {
      pricesData.forEach(p => {
        if (!pricesMap[p.product_id] || p.unit_price_pair < pricesMap[p.product_id]) {
          pricesMap[p.product_id] = p.unit_price_pair;
        }
      });
    }

    // Combine all data
    let enrichedProducts: Product[] = productsData.map(p => ({
      ...p,
      brand_name: p.brand_id ? brandsMap[p.brand_id] : undefined,
      category_name: p.category_id ? categoriesMap[p.category_id] : undefined,
      min_price_pair: pricesMap[p.id] || undefined,
    }));

    // Client-side sorting for price-based sorts
    if (sortBy === 'price_asc') {
      enrichedProducts.sort((a, b) => {
        const priceA = a.min_price_pair || Infinity;
        const priceB = b.min_price_pair || Infinity;
        return priceA - priceB;
      });
    } else if (sortBy === 'price_desc') {
      enrichedProducts.sort((a, b) => {
        const priceA = a.min_price_pair || 0;
        const priceB = b.min_price_pair || 0;
        return priceB - priceA;
      });
    }

    setProducts(enrichedProducts);
  };

  const updateFilter = (key: string, value: string) => {
    const newParams = new URLSearchParams(searchParams);
    if (value) {
      newParams.set(key, value);
    } else {
      newParams.delete(key);
    }
    // Reset to page 1 when filters change
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const clearAllFilters = () => {
    setSearchParams({});
  };

  const totalPages = Math.ceil(totalCount / pageSize);
  const hasActiveFilters = searchKeyword || brandFilter || categoryFilter || minStockFilter || sortBy !== 'default';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">All Products</h1>
            <p className="text-gray-600">Browse all available outdoor shoes, carton-based wholesale only.</p>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              {loading ? (
                <span>Loading...</span>
              ) : (
                <span className="font-medium">
                  {totalCount} product{totalCount !== 1 ? 's' : ''} found
                </span>
              )}
            </div>
            <div className="hidden lg:flex items-center gap-2 bg-white border border-gray-200 rounded-lg p-1">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded cursor-pointer transition-colors ${
                  viewMode === 'grid'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="Grid View"
              >
                <i className="ri-grid-line text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded cursor-pointer transition-colors ${
                  viewMode === 'list'
                    ? 'bg-blue-600 text-white'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title="List View"
              >
                <i className="ri-list-check text-lg w-5 h-5 flex items-center justify-center"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Filter & Sort Toolbar */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex flex-col lg:flex-row gap-4">
            {/* Search */}
            <div className="flex-1">
              <div className="relative">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                <input
                  type="text"
                  placeholder="Search by title or SKU"
                  value={searchKeyword}
                  onChange={(e) => updateFilter('search', e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Desktop Filters */}
            <div className="hidden lg:flex items-center gap-3">
              {/* Brand */}
              <select
                value={brandFilter}
                onChange={(e) => updateFilter('brand', e.target.value ? Number(e.target.value) : null)}
                className="px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Brands</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>
                    {brand.name}
                  </option>
                ))}
              </select>

              {/* Category */}
              <select
                value={categoryFilter}
                onChange={(e) => updateFilter('category', e.target.value ? Number(e.target.value) : null)}
                className="px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Categories</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>

              {/* Stock Filter */}
              <select
                value={minStockFilter}
                onChange={(e) => updateFilter('minStock', Number(e.target.value))}
                className="px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="0">All Stock</option>
                <option value="10">≥ 10 cartons</option>
                <option value="50">≥ 50 cartons</option>
                <option value="100">≥ 100 cartons</option>
              </select>

              {/* Page Size */}
              <select
                value={pageSize}
                onChange={(e) => updateFilter('pageSize', e.target.value)}
                className="px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="24">24 per page</option>
                <option value="48">48 per page</option>
                <option value="96">96 per page</option>
              </select>

              {/* Sort */}
              <select
                value={sortBy}
                onChange={(e) => updateFilter('sort', e.target.value)}
                className="px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="default">Default</option>
                <option value="price_asc">Price: Low to High</option>
                <option value="price_desc">Price: High to Low</option>
                <option value="stock_desc">Stock: High to Low</option>
              </select>

              {/* Clear All */}
              {hasActiveFilters && (
                <button
                  onClick={clearAllFilters}
                  className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-filter-off-line mr-1"></i>
                  Clear All
                </button>
              )}
            </div>

            {/* Mobile Filter Button */}
            <button
              onClick={() => setShowMobileFilters(true)}
              className="lg:hidden flex items-center justify-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer"
            >
              <i className="ri-filter-3-line"></i>
              Filter & Sort
              {hasActiveFilters && (
                <span className="ml-1 px-2 py-0.5 bg-blue-600 text-white text-xs rounded-full">
                  {Object.values({ selectedBrand: brandFilter, selectedCategory: categoryFilter, minStock: minStockFilter }).filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex gap-6">
          {/* Left Sidebar - Desktop Only */}
          <aside className="hidden lg:block w-64 flex-shrink-0">
            <ProductsFilterSidebar
              brands={brands}
              categories={categories}
              selectedBrand={brandFilter}
              selectedCategory={categoryFilter}
              minStock={minStockFilter}
              onBrandChange={(brandId) => updateFilter('brand', String(brandId))}
              onCategoryChange={(categoryId) => updateFilter('category', String(categoryId))}
              onMinStockChange={(stock) => updateFilter('minStock', String(stock))}
              onClearAll={clearAllFilters}
              hasActiveFilters={hasActiveFilters}
            />
          </aside>

          {/* Products List */}
          <div className="flex-1">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <div className="text-center">
                  <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin mb-4"></i>
                  <p className="text-gray-600">Loading products...</p>
                </div>
              </div>
            ) : error ? (
              <ErrorState onRetry={loadData} />
            ) : products.length === 0 ? (
              <EmptyState
                type={hasActiveFilters ? 'no-results' : 'no-products'}
                onClearFilters={clearAllFilters}
              />
            ) : (
              <>
                {/* Products Grid/List */}
                <div
                  className={
                    viewMode === 'grid'
                      ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'
                      : 'space-y-4'
                  }
                >
                  {products.map((product) => (
                    <ProductCard
                      key={product.id}
                      product={product}
                      viewMode={viewMode}
                      onAddToCart={loadProducts}
                    />
                  ))}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-8 flex items-center justify-center gap-4">
                    <button
                      onClick={() => updateFilter('page', String(currentPage - 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-arrow-left-line mr-2"></i>
                      Previous
                    </button>

                    <span className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>

                    <button
                      onClick={() => updateFilter('page', String(currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer whitespace-nowrap"
                    >
                      Next
                      <i className="ri-arrow-right-line ml-2"></i>
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Filter Drawer */}
      {showMobileFilters && (
        <div
          className="fixed inset-0 bg-black/50 z-50 lg:hidden"
          onClick={() => setShowMobileFilters(false)}
        >
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
              <h3 className="text-lg font-bold text-gray-900">Filter & Sort</h3>
              <button
                onClick={() => setShowMobileFilters(false)}
                className="w-8 h-8 flex items-center justify-center text-gray-500 hover:bg-gray-100 rounded-full cursor-pointer"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Search */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
                  <input
                    type="text"
                    placeholder="Search by title or SKU"
                    value={searchKeyword}
                    onChange={(e) => updateFilter('search', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm"
                  />
                </div>
              </div>

              {/* Brand */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Brand</label>
                <select
                  value={brandFilter}
                  onChange={(e) => updateFilter('brand', e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer"
                >
                  <option value="">All Brands</option>
                  {brands.map((brand) => (
                    <option key={brand.id} value={brand.id}>
                      {brand.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select
                  value={categoryFilter}
                  onChange={(e) => updateFilter('category', e.target.value ? Number(e.target.value) : null)}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Stock Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Stock</label>
                <select
                  value={minStockFilter}
                  onChange={(e) => updateFilter('minStock', Number(e.target.value))}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer"
                >
                  <option value="0">All Stock</option>
                  <option value="10">≥ 10 cartons</option>
                  <option value="50">≥ 50 cartons</option>
                  <option value="100">≥ 100 cartons</option>
                </select>
              </div>

              {/* Page Size */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Items per page</label>
                <select
                  value={pageSize}
                  onChange={(e) => updateFilter('pageSize', e.target.value)}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer"
                >
                  <option value="24">24 per page</option>
                  <option value="48">48 per page</option>
                  <option value="96">96 per page</option>
                </select>
              </div>

              {/* Sort */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Sort by</label>
                <select
                  value={sortBy}
                  onChange={(e) => updateFilter('sort', e.target.value)}
                  className="w-full px-4 py-2 pr-8 border border-gray-300 rounded-lg text-sm bg-white cursor-pointer"
                >
                  <option value="default">Default</option>
                  <option value="price_asc">Price: Low to High</option>
                  <option value="price_desc">Price: High to Low</option>
                  <option value="stock_desc">Stock: High to Low</option>
                </select>
              </div>
            </div>

            <div className="sticky bottom-0 bg-white border-t border-gray-200 px-6 py-4 flex gap-3">
              {hasActiveFilters && (
                <button
                  onClick={() => {
                    clearAllFilters();
                    setShowMobileFilters(false);
                  }}
                  className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Clear All
                </button>
              )}
              <button
                onClick={() => setShowMobileFilters(false)}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Apply Filters
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AllProductsPage;
